<script setup>
import { computed, onMounted, watch, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useFacilityStore } from '@/stores/facility.js'
import {useToast} from "primevue";
import Card from 'primevue/card';
import SpeedDial from 'primevue/speeddial';
import Contacts from "@/components/Contacts.vue";
import FacilityForm from '@/components/facility/FacilityForm.vue';
import ConfirmDeleteDialog from '@/components/common/ConfirmDeleteDialog.vue';
import {fancyDate} from "@/filters/fancyDate.js";
import { useResponsiblesStore } from '@/stores/responsibles.js';
import ResponsibilityList from '@/components/common/ResponsibilityList.vue';
import CreateReminderDialog from "@/components/common/CreateReminderDialog.vue";
import EntityMessageDialog from "@/components/messages/EntityMessageDialog.vue";
import TaskCreateDialog from '@/components/tasks/TaskCreateDialog.vue'
import ConfirmArchiveDialog from "@/components/common/ConfirmArchiveDialog.vue";
import EventHistoryDialog from "@/components/common/EventHistoryDialog.vue";
import { usePermissions } from '@/composables/usePermissions.js';


const route = useRoute()
const router = useRouter()
const facilityStore = useFacilityStore()
const responsiblesStore = useResponsiblesStore();
const { hasPermission } = usePermissions();

const showEditDialog = ref(false)
const showConfirm = ref(false)
const showConfirmArchive = ref(false)
const showConfirmUnarchive = ref(false)
const showMessageDialog = ref(false)
const showCreateTask = ref(false)
const showEventHistory = ref(false)



const toast = useToast();

const facility = computed(() => facilityStore.facility)
const facilityId = computed(() => facility.value?.id);
const facilityResponsiblesRaw = computed(() => responsiblesStore.items('facility', facilityId.value))
const isArchived = computed(() => facility.value?.status === 'ARCHIVED')
const responsible = computed(() => {
  const list = facilityResponsiblesRaw.value || []
  if (!Array.isArray(list) || list.length === 0) {
    return { id: null, name: '' }
  }
  const item = list.find(r => r?.is_primary) ?? list[0]
  const u = item?.user
  const id = Number(u?.id)
  const name = u?.full_name
  || [u?.last_name, u?.first_name].filter(Boolean).join(' ')
  || u?.name
  || ''
  return { id: Number.isFinite(id) && id > 0 ? id : null, name }
})


function openResponsibleMessage() {
  if (!responsible.value?.id) {
    toast.add({ severity: 'warn', summary: 'Не указан ответственный', life: 2000 })
    return
  }
  showMessageDialog.value = true
}

onMounted(() => {
  facilityStore.loadFacility(route.params.id)
})
watch(() => route.params.id, (id) => facilityStore.loadFacility(id))

watch(facilityId, (id) => {
  if (id) responsiblesStore.loadCurrent('facility', id);
}, { immediate: true });



function truncate(text, max = 100) {
  const s = text == null ? '' : String(text);
  return s.length > max ? s.slice(0, max) + '...' : s;
}

async function onArchiveConfirm() {
  try {
    await facilityStore.archiveFacility(route.params.id);
    toast.add({
      severity: 'success',
      summary: 'Объект архивирован',
      detail: 'Объект перемещён в архив',
      life: 2500,
    });
    showConfirm.value = false;
    await router.push('/facilities');
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось архивировать объект',
      life: 4000,
    });
  }
}

async function onUnarchiveConfirm() {
  try {
    await facilityStore.unarchiveFacility(route.params.id)
    toast.add({
      severity: 'success',
      summary: 'Объект возвращён из архива',
      detail: 'Статус объекта изменён на ACTIVE',
      life: 2500,
    })
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось вернуть объект из архива',
      life: 4000,
    })
  }
}

const blockerLabels = {
  works: 'Работы',
  contracts: 'Договоры',
  contacts: 'Контакты',
  comments: 'Комментарии'
}

function formatBlockers(blockers) {
  if (!blockers || typeof blockers !== 'object') return ''
  return Object.entries(blockers)
    .map(([key, count]) => `${blockerLabels[key] || key} (${count})`)
    .join('\n')
}

async function onDeleteConfirm() {
  try {
    await facilityStore.deleteFacility(route.params.id)
    toast.add({
      severity: 'success',
      summary: 'Объект удален',
      detail: 'Данные объекта успешно удалены',
      life: 2500,
    });
    await router.push('/facilities')
  } catch (e) {
    if (e.response?.status === 409 && e.response?.data?.blockers) {
      const data = e.response.data
      const blockersText = formatBlockers(data.blockers)
      const detail = `Найдены связанные сущности.\n${blockersText}\n Используйте архивирование.`
      toast.add({
        severity: 'warn',
        summary: 'Удаление невозможно',
        detail,
        life: 6000,
      });
    } else {
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось удалить объект',
        life: 4000,
      });
    }
  }
}

const showCreateReminder = ref(false)

const reminderTarget = computed(() => {
  if (!facilityId.value) return null
  return { app: 'facility', model: 'facility', objectId: Number(facilityId.value) }
})

const taskContext = computed(() => {
  if (!facilityId.value) return null
    return { app: 'facility', model: 'facility', objectId: Number(facilityId.value) }
})

const menuItems = computed(() => {
  const items = []

  // Удалить - требует facility.delete_facility
  if (hasPermission('facility.delete_facility')) {
    items.push({
      label: 'Удалить',
      icon: 'pi pi-trash',
      severity: 'danger',
      command: () => {
        showConfirm.value = true
      }
    })
  }

  // Архивировать/Вернуть из архива - требует facility.archive_facility
  if (hasPermission('facility.archive_facility')) {
    items.push({
      label: isArchived.value ? 'Вернуть из архива' : 'Архивировать',
      icon: isArchived.value ? 'pi pi-sun' : 'pi pi-moon',
      severity: isArchived.value ? 'success' : 'danger',
      command: () => {
        const currentStatus = isArchived.value
        if (currentStatus) {
          showConfirmUnarchive.value = true
        } else {
          showConfirmArchive.value = true
        }
      }
    })
  }

  // Написать ответственному - доступно всем
  items.push({
    label: 'Написать ответственному',
    icon: 'pi pi-envelope',
    command: openResponsibleMessage
  })

  // Создать напоминание - доступно всем
  items.push({
    label: 'Создать напоминание',
    icon: 'pi pi-bell',
    command: () => {
      showCreateReminder.value = true
    }
  })

  // Создать задачу - требует task.create_for_facility
  if (hasPermission('task.create_for_facility')) {
    items.push({
      label: 'Создать задачу',
      icon: 'pi pi-pen-to-square',
      command: () => { showCreateTask.value = true }
    })
  }

  // История - доступна всем
  items.push({
    label: 'История',
    icon: 'pi pi-history',
    command: () => {
      showEventHistory.value = true
    }
  })

  // Редактировать - требует facility.change_facility
  if (hasPermission('facility.change_facility')) {
    items.push({
      label: 'Редактировать',
      icon: 'pi pi-pencil',
      command: () => {
        showEditDialog.value = true
      }
    })
  }

  return items
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4">
      <Card>
        <template #title>
          <div class="flex justify-between gap-2 items-center relative">
            <span>{{ truncate(facility?.short_name || '...', 70) }}</span>
            <div style="position: relative; width: 44px; height: 44px;">
              <SpeedDial
                  :model="menuItems"
                  :radius="130"
                  type="quarter-circle"
                  direction="down-left"
                  :style="{ position: 'absolute', left: 0, bottom: 0 }"
                  buttonClass="p-button-text p-button-secondary"
                  :showIcon="'pi pi-ellipsis-v'"
                  :hideIcon="'pi pi-times'"
              />
            </div>
          </div>
        </template>
        <template #content>
          <div class="space-y-4">
            <div class="flex">
              <div class="w-4/5 space-y-4">
                <div>
                  <span class="font-semibold">Адрес: </span>{{ facility?.address }}
                </div>
                <div>
                  <span class="font-semibold">Название: </span>{{ facility?.name || '' }}
                </div>

              </div>
              <div class="text-right text-sm w-1/5">
                <p v-if="facility?.term_start" class="flex items-center justify-end gap-2 text-orange-400">
                  <i class="pi pi-flag text-xs"></i>
                  <span>{{ fancyDate(facility?.term_start, 'short') }}</span>
                </p>
                <p v-if="facility?.term_end" class="flex items-center justify-end gap-2 text-lime-600 mt-2">
                  <i class="pi pi-flag-fill text-xs"></i>
                  <span>{{ fancyDate(facility?.term_end, 'short') }}</span>
                </p>
              </div>
            </div>
            <ResponsibilityList
                :list="facilityResponsiblesRaw"
                singularTitle="Ответственный"
                pluralTitle="Ответственные"
                :removable="true"
                entityType="facility"
                :entityId="facilityId"
                :showPriority="false"
                :allowAdd="true"

            />
            <Contacts v-if="facility?.id" target="facility" :id="facility.id" />
          </div>
        </template>
      </Card>
    </div>

    <FacilityForm
        v-model:visible="showEditDialog"
        :facility="facility"
        @saved="() => facilityStore.loadFacility(route.params.id)"
    />
    <ConfirmDeleteDialog
        v-model:visible="showConfirm"
        :facilityName="facility?.short_name || facility?.name || ''"
        @confirm="onDeleteConfirm"
    />
    <ConfirmArchiveDialog
        v-model:visible="showConfirmArchive"
        mode="archive"
        :facilityName="facility?.short_name || facility?.name || ''"
        @confirm="onArchiveConfirm"
    />
    <ConfirmArchiveDialog
        v-model:visible="showConfirmUnarchive"
        mode="unarchive"
        :facilityName="facility?.short_name || facility?.name || ''"
        @confirm="onUnarchiveConfirm"
    />
    <CreateReminderDialog
        v-if="reminderTarget"
        v-model="showCreateReminder"
        :targetAppLabel="reminderTarget.app"
        :targetModel="reminderTarget.model"
        :targetObjectId="reminderTarget.objectId"
    />
    <EntityMessageDialog
        v-model="showMessageDialog"
        v-if="facility"
        :user-id="responsible.id"
        :responsible-name="responsible.name || 'Ответственный'"
        entity-type="facility"
        :entity-id="facility?.id"
        :entity-title="facility?.short_name || facility?.name || `#${facility?.id}`"
        :entity-url="`/facilities/${facility?.id}`"
    />
    <TaskCreateDialog
        v-if="taskContext"
        v-model="showCreateTask"
        :contextAppLabel="taskContext.app"
        :contextModel="taskContext.model"
        :contextId="taskContext.objectId"
        :defaultAssigneeId="responsible.id"
        :contextTitle="facility?.short_name || facility?.name || `#${facilityId}`"
        @created="() => {/* опционально: обновить локальный список задач блока сущности, если он есть */}"
        />
    <EventHistoryDialog
        v-model:visible="showEventHistory"
        target="facility"
        :entityId="facilityId"
        :entityName="facility?.name"
    />
  </div>
</template>

<style scoped>
:deep(.p-card-body) {
  padding-top: 0;
  padding-bottom: 8px;
}
</style>